import './api/apiClient';
import './jobseeker-v2/workspaceKitTheme.css';
import { Suspense, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { initPostHog } from './telemetry/posthog';
import AppShell from './components/AppShell';
import ExternalRedirect from './components/ExternalRedirect';
import LegacyHostRedirect from './components/LegacyHostRedirect';
import LegacyPathRedirect from './components/LegacyPathRedirect';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoadingScreen from './components/LoadingScreen';
import AppErrorBoundary from './components/AppErrorBoundary';
import AppNotifications from './components/AppNotifications';
import SessionBridge from './components/SessionBridge';
import { isAuth0Configured } from './auth/auth0Config';
import lazyWithReload from './utils/lazyWithReload';
import { getAppSurface } from './config/appSurface';
import {
  loginModeForSurface,
  resolvePortalPublicUrl,
  resolvePostLoginPath,
  shouldMountAuth0,
} from './config/appSurfaceRoutes';
// Surface-level code splitting (RSW-609): each workspace loads as its own
// chunk, so mobile-first jobseekers stop downloading counselor/admin code.
// ProtectedRoute resolves auth before rendering children, so the lazy fetch
// only starts once the session says this surface is the right one.
// lazyWithReload recovers from stale hashed chunks after a deploy.
const JobseekerWorkspace = lazyWithReload(() => import('./jobseeker-v2/JobseekerWorkspace'));
const AssessmentsHub = lazyWithReload(() => import('./jobseeker-v2/AssessmentsHub'));
const CounselorWorkspace = lazyWithReload(() => import('./counselor-v1/CounselorWorkspace'));
const CounselorEditWorkspace = lazyWithReload(() => import('./counselor-v1/CounselorEditWorkspace'));
const AdminWorkspace = lazyWithReload(() => import('./admin-v1/AdminWorkspace'));

// The Auth0 SDK only mounts on counselor SSO surfaces (portal/admin/all —
// never the jobseeker host), so jobseekers skip the whole library.
const Auth0AppProvider = lazyWithReload(() => import('./auth/Auth0AppProvider'));
const Auth0SessionBridge = lazyWithReload(() => import('./auth/Auth0SessionBridge'));

// Speculative preload: when the visit is clearly headed at a jobseeker screen,
// start downloading that screen's chunk now so it streams in parallel with the
// auth roundtrip instead of chaining after it. Wrong guesses just warm a
// cache; ProtectedRoute still decides what renders. Failures are ignored —
// the real lazy import (with stale-deploy recovery) is the source of truth.
const WORKSPACE_ENTRY_PATH = /^\/(access\/|workspace|tracker|jobs$|profile|settings|cover|interview)/;
if (typeof window !== 'undefined') {
  if (WORKSPACE_ENTRY_PATH.test(window.location.pathname)) {
    import('./jobseeker-v2/JobseekerWorkspace').catch(() => {});
  } else if (window.location.pathname.startsWith('/assessments')) {
    import('./jobseeker-v2/AssessmentsHub').catch(() => {});
  }
}

// PostHog telemetry (RSW-591) — initialized at module scope, outside the
// fallible route subtree, so a crash during the initial render still gets
// captured (pre-init exceptions buffer until the deferred init completes).
// Safe no-op without a key.
initPostHog();

function PostLoginNavigate({ userType, userRole, fallback = '/' }) {
  const target = resolvePostLoginPath({ userType, userRole });
  if (/^https?:\/\//i.test(target)) {
    return (
      <ExternalRedirect
        url={target}
        fallback={fallback}
        label="Opening the right workspace…"
      />
    );
  }
  return <Navigate to={target || fallback} replace />;
}

// Exported for RSW-598 session-recovery tests.
export function ProtectedRoute({ children, allowedTypes, allowedRoles }) {
  const { user, userType, userRole, loading } = useAuth();
  const previousJobseekerRef = useRef(null);

  if (user && userType === 'JOBSEEKER') {
    previousJobseekerRef.current = {
      guid: typeof user.guid === 'string' ? user.guid.trim() : '',
      email: typeof user.email === 'string' ? user.email.trim() : '',
    };
  }

  if (loading) {
    return <LoadingScreen label="Checking your session..." />;
  }

  if (!user) {
    const previous = previousJobseekerRef.current;
    previousJobseekerRef.current = null;
    // Quiet /me logout still offers recovery (RSW-398). SessionBridge expiry
    // already navigates with state; this covers ProtectedRoute drops.
    if (previous?.guid || previous?.email) {
      return (
        <Navigate
          to="/"
          replace
          state={{
            sessionExpired: true,
            audience: 'JOBSEEKER',
            resumeGuid: previous.guid || null,
            resumeEmail: previous.email || null,
          }}
        />
      );
    }
    return <Navigate to="/" replace />;
  }

  if (allowedTypes && !allowedTypes.includes(userType)) {
    // Send each persona to their own home. A jobseeker must never land on
    // counselor caseload via a type mismatch (RSW-446).
    return <PostLoginNavigate userType={userType} userRole={userRole} />;
  }

  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return <Navigate to="/caseload" replace />;
  }

  return children;
}

function AdminProtectedRoute({ children }) {
  const { user, userType, userRole, loading } = useAuth();
  const surface = getAppSurface();

  if (loading) {
    return <LoadingScreen label="Checking your session..." />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (userType !== 'COUNSELOR') {
    return <PostLoginNavigate userType={userType} userRole={userRole} />;
  }

  if (userRole !== 'PLATFORM_ADMIN') {
    if (surface === 'admin') {
      const portalUrl = resolvePortalPublicUrl();
      if (portalUrl) {
        return (
          <ExternalRedirect
            url={portalUrl}
            label="Returning to counselor portal…"
          />
        );
      }
      return (
        <div className="csl-empty" style={{ padding: '2rem', maxWidth: '32rem', margin: '2rem auto' }}>
          <p>Platform admin access is required on this site.</p>
          <p>Use the counselor portal to sign in with your workforce account.</p>
        </div>
      );
    }
    return <Navigate to="/caseload" replace />;
  }

  return children;
}

function RootRoute() {
  const { user, userType, userRole, loading } = useAuth();

  if (loading) {
    return <LoadingScreen label="Loading RS Works..." />;
  }

  if (!user) {
    return <AppShell loginMode={loginModeForSurface()} />;
  }

  const target = resolvePostLoginPath({ userType, userRole });
  if (/^https?:\/\//i.test(target)) {
    return (
      <ExternalRedirect
        url={target}
        fallback="/"
        label="Opening counselor portal…"
      />
    );
  }

  return <Navigate to={target} replace />;
}

/** @returns {import('react').ReactElement} */
function legacyPathRedirect(path) {
  return <Route key={`legacy-${path}`} path={path} element={<LegacyPathRedirect />} />;
}

/** Route fragments must be direct children of `<Routes>` (React Router v7). */
function jobseekerRouteElements(surface) {
  const accessLoginMode = surface === 'jobseeker' ? 'jobseeker' : 'all';

  return (
    <>
      <Route path="/access/:guid" element={<AppShell loginMode={accessLoginMode} />} />

      <Route path="/workspace" element={
        <ProtectedRoute allowedTypes={['JOBSEEKER']}>
          <JobseekerWorkspace />
        </ProtectedRoute>
      } />

      <Route path="/tracker" element={
        <ProtectedRoute allowedTypes={['JOBSEEKER']}>
          <JobseekerWorkspace initialModule="tracker" />
        </ProtectedRoute>
      } />

      <Route path="/jobs" element={
        <ProtectedRoute allowedTypes={['JOBSEEKER']}>
          <JobseekerWorkspace initialModule="jobs" />
        </ProtectedRoute>
      } />

      <Route path="/profile" element={
        <ProtectedRoute allowedTypes={['JOBSEEKER']}>
          <JobseekerWorkspace initialModule="profile" />
        </ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute allowedTypes={['JOBSEEKER']}>
          <JobseekerWorkspace initialModule="settings" />
        </ProtectedRoute>
      } />

      <Route path="/cover" element={
        <ProtectedRoute allowedTypes={['JOBSEEKER']}>
          <JobseekerWorkspace initialModule="cover" />
        </ProtectedRoute>
      } />

      <Route path="/interview" element={
        <ProtectedRoute allowedTypes={['JOBSEEKER']}>
          <JobseekerWorkspace initialModule="interview" />
        </ProtectedRoute>
      } />

      <Route path="/assessments" element={
        <ProtectedRoute allowedTypes={['JOBSEEKER']}>
          <AssessmentsHub />
        </ProtectedRoute>
      } />

      {legacyPathRedirect('/app/home')}
      {legacyPathRedirect('/app/build')}
      {legacyPathRedirect('/app/workspace')}
      {legacyPathRedirect('/app/tracker')}
      {legacyPathRedirect('/app/jobs')}
      {legacyPathRedirect('/app/profile')}
      {legacyPathRedirect('/app/settings')}
      {legacyPathRedirect('/app/cover')}
      {legacyPathRedirect('/app/interview')}
      {legacyPathRedirect('/app/assessments')}
      {legacyPathRedirect('/app/legacy')}
      {legacyPathRedirect('/app')}
      {surface === 'jobseeker' ? legacyPathRedirect('/dashboard') : null}
    </>
  );
}

function portalRouteElements() {
  return (
    <>
      <Route path="/caseload" element={
        <ProtectedRoute allowedTypes={['COUNSELOR']}>
          <CounselorWorkspace view="caseload" />
        </ProtectedRoute>
      } />

      <Route path="/job-board" element={
        <ProtectedRoute allowedTypes={['COUNSELOR']}>
          <CounselorWorkspace view="job-board" />
        </ProtectedRoute>
      } />

      <Route path="/team" element={
        <ProtectedRoute allowedTypes={['COUNSELOR']} allowedRoles={['PARTNER_ADMIN', 'PLATFORM_ADMIN']}>
          <CounselorWorkspace view="team" />
        </ProtectedRoute>
      } />

      <Route path="/account" element={
        <ProtectedRoute allowedTypes={['COUNSELOR']}>
          <CounselorWorkspace view="account" />
        </ProtectedRoute>
      } />

      <Route path="/jobseekers/:id" element={
        <ProtectedRoute allowedTypes={['COUNSELOR']}>
          <CounselorWorkspace view="detail" />
        </ProtectedRoute>
      } />

      <Route path="/jobseekers/:id/library" element={
        <ProtectedRoute allowedTypes={['COUNSELOR']}>
          <CounselorWorkspace view="library" />
        </ProtectedRoute>
      } />

      <Route path="/jobseekers/:id/mirror" element={
        <ProtectedRoute allowedTypes={['COUNSELOR']}>
          <CounselorWorkspace view="mirror" />
        </ProtectedRoute>
      } />

      {/* Edit on behalf (UX 2.8 / #1060): a counselor opens the jobseeker's own
          workspace to make changes for them, staying signed in. COUNSELOR-only;
          the server enforces caseload scope on every call. */}
      {/* One route with an optional segment, so switching modules keeps the
          workspace mounted instead of remounting it. */}
      <Route path="/jobseekers/:id/edit/:module?" element={
        <ProtectedRoute allowedTypes={['COUNSELOR']}>
          <CounselorEditWorkspace />
        </ProtectedRoute>
      } />

      {legacyPathRedirect('/dashboard')}
      {legacyPathRedirect('/counselor')}
      {legacyPathRedirect('/counselor/caseload')}
      {legacyPathRedirect('/counselor/team')}
      <Route path="/counselor/jobseekers/:id/library" element={<LegacyPathRedirect />} />
      <Route path="/counselor/jobseekers/:id/mirror" element={<LegacyPathRedirect />} />
      <Route path="/counselor/jobseekers/:id" element={<LegacyPathRedirect />} />
    </>
  );
}

function adminRouteElements(AdminGuard, adminRoleProps) {
  return (
    <>
      <Route path="/admin" element={
        <AdminGuard {...adminRoleProps}>
          <Navigate to="/admin/partners" replace />
        </AdminGuard>
      } />

      <Route path="/admin/:view" element={
        <AdminGuard {...adminRoleProps}>
          <AdminWorkspace />
        </AdminGuard>
      } />
    </>
  );
}

function AppRoutes() {
  const surface = getAppSurface();

  useEffect(() => {
    ReactGA.initialize([{ trackingId: 'G-5KZ373JDL7' }]);
  }, []);

  const showJobseeker = surface === 'all' || surface === 'jobseeker';
  const showPortal = surface === 'all' || surface === 'portal';
  const showAdmin = surface === 'all' || surface === 'admin';

  const AdminGuard = surface === 'admin' ? AdminProtectedRoute : ProtectedRoute;
  const adminRoleProps = surface === 'admin'
    ? {}
    : { allowedTypes: ['COUNSELOR'], allowedRoles: ['PLATFORM_ADMIN'] };

  return (
    <Suspense fallback={<LoadingScreen label="Loading your workspace..." />}>
      <Routes>
        <Route path="/" element={<RootRoute />} />

        {showJobseeker ? jobseekerRouteElements(surface) : null}
        {showPortal ? portalRouteElements() : null}
        {showAdmin ? adminRouteElements(AdminGuard, adminRoleProps) : null}

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function AppAuthProviders({ children }) {
  const mountAuth0 = shouldMountAuth0() && isAuth0Configured();

  if (!mountAuth0) {
    return (
      <>
        <SessionBridge />
        {children}
      </>
    );
  }

  return (
    <Suspense fallback={<LoadingScreen label="Loading RS Works..." />}>
      <Auth0AppProvider>
        <SessionBridge />
        <Auth0SessionBridge />
        {children}
      </Auth0AppProvider>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppErrorBoundary>
        <AppNotifications />
        <BrowserRouter>
          <LegacyHostRedirect>
            <AppAuthProviders>
              <AppRoutes />
            </AppAuthProviders>
          </LegacyHostRedirect>
        </BrowserRouter>
      </AppErrorBoundary>
    </AuthProvider>
  );
}

export default App;
